import type { ReactNode } from 'react';
import { Paragraph, XStack, YStack } from 'tamagui';

import { BackLink } from '@/components/back-link';
import { Heading } from '@/components/heading';

// Issue #555 (founder decision 5, docs/internal/ui-decisions.md): one
// shared screen header -- Back left, optional action right, then the title
// below on its own row -- on every screen. The whole-app UI critique measured Back starting at x=16/17/24
// and titles at y=75/108/120 depending on screen: each screen hand-rolled
// the same BackLink + Heading stack with its own inset, so nothing
// aligned. This component owns that layout; the caller owns the gutter
// (the 24px page inset, space token "$6" -- decision 5's one gutter)
// exactly as it already owns its outer padding today, so this component
// carries no horizontal padding of its own and never passes BackLink's
// `edgePadding` (adding it inside a guttered container would double the
// inset -- see BackLink's own comment).
//
// `title`/`action` are optional because the real screens are not all
// back+title: thread-detail and reveal-requests render Back + a refresh
// button with the title further down in the list (verified per screen
// before writing this); messages and profile-view render Back alone. A
// required title would have forced a duplicated heading on exactly the
// screens whose duplicate-title bug (see ScreenContainer's comment) was
// already fixed once.
export interface ScreenHeaderProps {
  title?: string;
  subtitle?: string;
  action?: ReactNode;
  label?: 'Back' | 'Cancel' | 'Close';
  onBack: () => void;
}

export function ScreenHeader({ title, subtitle, action, label = 'Back', onBack }: ScreenHeaderProps) {
  // Two rows, one left edge: Back (and the optional action) on a 44px row, then
  // the h1 and subtitle stacked below at the same x. No basis or wrap tricks:
  // the title never hangs off the Back link, at any width.
  return (
    <YStack gap="$2" pb={title === undefined ? '$2' : undefined}>
      <XStack alignItems="center" justifyContent="space-between" minHeight={44}>
        <BackLink label={label} onPress={onBack} />
        {action}
      </XStack>
      {title !== undefined && (
        <YStack gap="$2" minWidth={0}>
          <Heading>{title}</Heading>
          {subtitle !== undefined && <Paragraph color="$colorSecondary">{subtitle}</Paragraph>}
        </YStack>
      )}
    </YStack>
  );
}
